import styled from "styled-components";
import { useState, useEffect, useRef, useContext } from "react";
import { Context } from "../SchedulePlanner";
import {
  format,
  subDays,
  addDays,
  parseISO,
  eachDayOfInterval,
} from "date-fns";
import BarLoader from "react-spinners/BarLoader";
import {
  useFetchEntities,
  useFetchVehicleScheduleByDateRange,
} from "../../../../../reactQueryHooks";
import VehicleBox from "./VehicleBox";
import CalendarBox from "./CalendarBox";
import VehicleSelectDropdown from "./VehicleSelectDropdown";
import VehicleSelect from "./VehicleSelect";
import React from "react";
import "./style.css";

//----------------------------------------------------------------

const ContentWrapper = styled.div`
  flex: 1;
  display: flex;
  justify-content: center;
  overflow: hidden;
`;

const SpinnerContainer = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  align-self: center;
`;

const DateCell = styled.div<{
  isCurrentDay: boolean;
  isSelectedInTourGroups: boolean;
}>`
  background-color: ${(props) =>
    props.isCurrentDay ? "rgb(221 207 173)" : "#fcfcfc"};
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 11px;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
    color: rgb(26 99 172);
  }
  text-decoration: ${(props) =>
    props.isSelectedInTourGroups ? "underline" : "none"};
  color: ${(props) =>
    props.isSelectedInTourGroups ? "rgb(26 99 172)" : "black"};
`;

//------------------------------------------------------------------------

interface Vehicle {
  value: string;
  label: string;
}

interface Schedule {
  vehicle_id: string;
  date: string;
  [key: string]: any;
}

interface EntitiesData {
  data: {
    vehicles: Array<{
      _id: string;
      plate: string;
      [key: string]: any;
    }>;
    [key: string]: any;
  };
}

interface SchedulePlannerGridProps {
  targetDate: string;
  selectedDate?: string;
}

const VehiclePlannerGrid: React.FC<SchedulePlannerGridProps> = ({
  targetDate,
  selectedDate,
}) => {
  const { vehicle_ids, setVehicle_ids, tourGroupsDate, setTourGroupsDate } =
    useContext(Context);
  const date_range: string[] = eachDayOfInterval({
    start: subDays(new Date(targetDate), 7),
    end: addDays(new Date(targetDate), 7),
  }).map((date) => format(date, "yyyy-MM-dd"));

  const {
    data: entities = { data: { vehicles: [] } } as EntitiesData,
    isFetching: isFetchingEntities,
  } = useFetchEntities() as any;

  // Transform and sort staff in a single operation
  const vehicles: Vehicle[] = entities.data.vehicles.map((vehicle: any) => ({
    value: vehicle._id,
    label: vehicle.plate,
  }));

  const noUsersSelected = vehicle_ids.length === 0;

  const selectedVehicles: Vehicle[] = vehicle_ids
    .map((id) => {
      const user = vehicles.find((user) => user.value === id);
      return user
        ? {
            value: id,
            label: user.label,
          }
        : null;
    })
    .filter((user): user is Vehicle => user !== null);

  const {
    data: vehiclesScheduleData = [],
    isRefetching,
    isFetching: isFetchingVehiclesSchedule,
  } = useFetchVehicleScheduleByDateRange(date_range, vehicle_ids);

  //------------------------------------------------------------------------

  const listRef = useRef<HTMLDivElement>(null);
  const currentDayRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (currentDayRef.current) {
      currentDayRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
        inline: "center",
      });
    } else {
      if (listRef.current) {
        listRef.current.scrollLeft = 0;
      }
    }
  }, [vehiclesScheduleData, isRefetching, selectedDate]);

  //------------------------------------------------------------------------

  const [hoveredRow, setHoveredRow] = useState<string | null>(null);
  const [hoveredCol, setHoveredCol] = useState<number | null>(null);

  return (
    <ContentWrapper>
      {!isFetchingEntities &&
        !isFetchingVehiclesSchedule &&
        !isRefetching &&
        !noUsersSelected && (
          <div
            ref={listRef}
            className="grid-container"
            style={{
              gridTemplateColumns: `160px repeat(${date_range.length}, 175px)`,
              gridTemplateRows: `35px repeat(${selectedVehicles.length}, 135px)`,
            }}
          >
            <VehicleSelectDropdown
              vehicle_ids={vehicle_ids}
              setVehicle_ids={setVehicle_ids}
            />

            {date_range.map((date, colIndex) => {
              const currentDay =
                selectedDate || format(new Date(), "yyyy-MM-dd");
              const isCurrentDay = date === currentDay;
              return (
                <DateCell
                  isSelectedInTourGroups={date === tourGroupsDate}
                  ref={isCurrentDay ? currentDayRef : null}
                  isCurrentDay={isCurrentDay}
                  key={colIndex}
                  className={`cell header-cell sticky-row ${
                    hoveredCol === colIndex ? "highlight" : ""
                  }`}
                  onClick={() => {
                    setTourGroupsDate(date);
                    localStorage.setItem("tourGroupsDate", date);
                  }}
                >
                  {format(parseISO(date), "EEE, dd MMM yy")}
                </DateCell>
              );
            })}

            {selectedVehicles.map((vehicle, rowIndex) => (
              <React.Fragment key={rowIndex}>
                <VehicleBox
                  isHovered={hoveredRow === vehicle.value}
                  vehicle={vehicle}
                />

                {date_range.map((date, colIndex) => {
                  const vehicleSchedule = vehiclesScheduleData?.filter(
                    (schedule: Schedule) =>
                      schedule?.vehicle_id === vehicle.value &&
                      schedule?.date === date
                  );

                  return (
                    <div
                      onMouseEnter={() => {
                        setHoveredRow(vehicle.value);
                        setHoveredCol(colIndex);
                      }}
                      onMouseLeave={() => {
                        setHoveredRow(null);
                        setHoveredCol(null);
                      }}
                      key={colIndex}
                      className="cell"
                    >
                      <CalendarBox
                        key={`${vehicle.value}-${date}`}
                        date={date}
                        vehicle_id={vehicle.value}
                        schedule={vehicleSchedule || null}
                      />
                    </div>
                  );
                })}
              </React.Fragment>
            ))}
          </div>
        )}
      {(isFetchingEntities || isFetchingVehiclesSchedule || isRefetching) && (
        <SpinnerContainer>
          <BarLoader color="#606060" loading={true} />
        </SpinnerContainer>
      )}
      {noUsersSelected &&
        !isFetchingEntities &&
        !isFetchingVehiclesSchedule &&
        !isRefetching && (
          <VehicleSelect
            vehicle_ids={vehicle_ids}
            setVehicle_ids={setVehicle_ids}
          />
        )}
    </ContentWrapper>
  );
};

export default VehiclePlannerGrid;
